import React from "react";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { money } from "../../lib/packageLogic";

// The panel that stays with them: what's in the package, how far through they are,
// and the price so far. Where the engine can't price the package yet it shows
// progress instead of a partial total, so no misleading figure is ever on screen.
export default function SummaryPanel({ services = [], quote, quoting, progress, outstanding = 0, emptyText = "", showProgress = true }) {
  const priced = quote && (quote.status === "priced" || quote.status === "priced_review");
  const monthly = quote?.totals?.monthly_recurring;

  return (
    <aside className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="pricing-summary-panel">
      <div className="px-5 py-4 border-b border-slate-100">
        <p className="font-display font-bold text-navy text-sm">Your package</p>
        <p className="text-xs text-slate-500 mt-0.5" data-testid="pricing-summary-count">
          {services.length} in your package
        </p>
      </div>

      <ul className="px-5 py-4 space-y-2.5">
        {services.map((s) => (
          <li key={s.service_id} className="flex items-start gap-2.5 text-sm" data-testid={`pricing-summary-service-${s.service_id}`}>
            {s.complete ? (
              <CheckCircle2 size={15} className="text-teal shrink-0 mt-0.5" />
            ) : (
              <Circle size={15} className="text-slate-300 shrink-0 mt-0.5" />
            )}
            <span className={s.complete ? "text-navy" : "text-slate-500"}>{s.name}</span>
          </li>
        ))}
      </ul>

      <div className="px-5 py-4 bg-slate-50 border-t border-slate-100">
        {quoting && !priced ? (
          <p className="text-xs text-slate-500 flex items-center gap-2" data-testid="pricing-summary-working">
            <Loader2 size={13} className="animate-spin" /> Working out your price…
          </p>
        ) : priced && monthly !== undefined ? (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Current estimate</p>
            <p className="font-display font-extrabold text-navy text-2xl mt-1" data-testid="pricing-summary-estimate">{money(monthly)}</p>
            <p className="text-xs text-slate-500">per month + VAT</p>
            <p className="text-[11px] text-slate-400 mt-2">Updates as you go</p>
          </>
        ) : (
          <>
            {showProgress && progress && progress.required > 0 && (
              <>
                <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden" aria-hidden="true">
                  <div className="h-full bg-orange duration-500 [transition-property:width]" style={{ width: `${progress.percent}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-2" data-testid="pricing-summary-progress">
                  {progress.answered} of {progress.required} answered
                </p>
              </>
            )}
            <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="pricing-summary-empty">
              {showProgress && outstanding > 0
                ? `${outstanding} more answer${outstanding === 1 ? "" : "s"} and we can price this.`
                : emptyText || "Your price will appear as you answer."}
            </p>
          </>
        )}
      </div>
    </aside>
  );
}
